import { auth, config } from './supabase'
import { apiFetch, isHonoApiMode } from './apiClient'
import { getHonoAccessToken } from './honoDataPlane'

export interface ApiResponse<T> {
    data: T | null
    error: {
        message: string
        code?: string
        trace_id: string
        request_id?: string
        status?: number
    } | null
}

interface InvokeOptions {
    body?: unknown
    method?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
    path?: string
    timeoutMs?: number
}

interface ApiErrorEnvelope {
    error?: unknown
    code?: unknown
    message?: unknown
    trace_id?: unknown
    request_id?: unknown
}

const SESSION_LOOKUP_TIMEOUT_MS = 5_000
const FETCH_TIMEOUT_MS = 15_000

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function toSafeMessage(value: unknown, fallback: string): string {
    return typeof value === 'string' && value.trim() ? value : fallback
}

function getProjectRef(supabaseUrl: string): string | null {
    try {
        const host = new URL(supabaseUrl).hostname
        const [projectRef] = host.split('.')
        return projectRef?.trim() || null
    } catch {
        return null
    }
}

function readAccessTokenFromStorage(): string | null {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return null

    const projectRef = getProjectRef(config.supabaseUrl)
    if (!projectRef) return null

    const raw = window.localStorage.getItem(`sb-${projectRef}-auth-token`)
    if (!raw) return null

    try {
        const parsed = JSON.parse(raw) as
            | { access_token?: unknown; currentSession?: { access_token?: unknown } | null; session?: { access_token?: unknown } | null }
            | null
        const directToken = typeof parsed?.access_token === 'string' ? parsed.access_token : null
        const currentSessionToken = typeof parsed?.currentSession?.access_token === 'string' ? parsed.currentSession.access_token : null
        const sessionToken = typeof parsed?.session?.access_token === 'string' ? parsed.session.access_token : null
        return directToken || currentSessionToken || sessionToken
    } catch {
        return null
    }
}

export async function resolveAccessToken(): Promise<string | null> {
    if (isHonoApiMode()) {
        const honoTok = getHonoAccessToken()
        if (honoTok) return honoTok
        // dual-run: fall through if supabase session still available
    }

    // Fast path: grab the token from localStorage (instant, avoids network round-trip)
    const cachedToken = readAccessTokenFromStorage()
    if (cachedToken) return cachedToken

    // Slow path: wait for Supabase to resolve/refresh the session (legacy dual-run)
    if (!config.honoMode || config.supabaseUrl) {
        try {
            const sessionResult = await Promise.race([
                auth.getSession(),
                new Promise<null>((resolve) => setTimeout(() => resolve(null), SESSION_LOOKUP_TIMEOUT_MS)),
            ])

            if (sessionResult && typeof sessionResult === 'object' && 'data' in sessionResult) {
                const accessToken = (sessionResult as { data?: { session?: { access_token?: string | null } | null } }).data?.session?.access_token
                if (typeof accessToken === 'string' && accessToken.trim()) return accessToken
            }
        } catch {
            // No session available
        }
    }

    return null
}

function normalizeErrorEnvelope(
    body: unknown,
    fallbackTraceId: string,
    status?: number
): { message: string; code: string; trace_id: string; request_id?: string; status?: number } {
    const envelope: ApiErrorEnvelope = isRecord(body) ? body : {}
    let code = toSafeMessage(envelope.error ?? envelope.code, 'api_error')
    let message = toSafeMessage(envelope.message, 'An unexpected server error occurred.')
    const trace_id = toSafeMessage(envelope.trace_id, fallbackTraceId)
    const request_id = typeof envelope.request_id === 'string' ? envelope.request_id : undefined

    if (status === 401 && code === 'api_error') {
        code = 'unauthorized'
        message = 'Your session has expired or is invalid. Please sign in again.'
    }
    if (status === 403 && code === 'api_error') {
        code = 'forbidden'
        message = 'You do not have permission to access this resource.'
    }

    return { message, code, trace_id, request_id, status }
}

/**
 * Safe wrapper for invoking Supabase Edge Functions.
 * Enforces standard error handling and prevents PHI leaks in logs.
 */
export async function invokeFunction<T>(
    functionName: string,
    options?: InvokeOptions
): Promise<ApiResponse<T>> {
    const trace_id = crypto.randomUUID()
    const method = options?.method ?? 'POST'
    const path = options?.path ? (options.path.startsWith('/') ? options.path : `/${options.path}`) : ''

    try {
        const accessToken = await resolveAccessToken()
        if (!accessToken) {
            return {
                data: null,
                error: {
                    code: 'auth_required',
                    message: 'Your session has expired. Please sign in again.',
                    trace_id,
                },
            }
        }

        // Hono staged path: VITE_API_BASE_URL (no Supabase functions host / apikey)
        if (isHonoApiMode()) {
            const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData
            const headers: Record<string, string> = {
                Authorization: `Bearer ${accessToken}`,
                'X-Trace-Id': trace_id,
                'X-Request-Id': trace_id,
            }
            if (!isFormData) headers['Content-Type'] = 'application/json'
            const controller = isFormData ? undefined : new AbortController()
            const fetchTimeout = controller ? setTimeout(() => controller.abort(), options?.timeoutMs ?? FETCH_TIMEOUT_MS) : undefined
            const honoPath = `/functions/v1/${functionName}${path}`
            const response = await apiFetch(honoPath, {
                method,
                headers,
                accessToken,
                ...(controller ? { signal: controller.signal } : {}),
                ...(method === 'GET' ? {} : {
                    body: isFormData ? options!.body as FormData : JSON.stringify(options?.body ?? {}),
                }),
            }).finally(() => { if (fetchTimeout) clearTimeout(fetchTimeout) })

            const responseBody = await response.json().catch(() => null)
            if (!response.ok) {
                console.error(`[API ERROR] ${functionName}`, { trace_id, status: response.status, via: 'hono' })
                const normalized = normalizeErrorEnvelope(responseBody, trace_id, response.status)
                return {
                    data: null,
                    error: {
                        message: normalized.message,
                        code: normalized.code,
                        trace_id: normalized.trace_id,
                        request_id: normalized.request_id,
                        status: normalized.status,
                    },
                }
            }
            return { data: responseBody as T, error: null }
        }

        // Use relative URL in dev mode to route through Vite proxy (avoids CORS)
        const baseUrl = import.meta.env.DEV ? '' : config.supabaseUrl
        const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData
        const headers: Record<string, string> = {
            apikey: config.supabaseAnonKey,
            Authorization: `Bearer ${accessToken}`,
            'X-Trace-Id': trace_id,
            'X-Request-Id': trace_id,
        }
        // Let the browser set Content-Type (with boundary) for FormData
        if (!isFormData) {
            headers['Content-Type'] = 'application/json'
        }
        const controller = isFormData ? undefined : new AbortController()
        const fetchTimeout = controller ? setTimeout(() => controller.abort(), options?.timeoutMs ?? FETCH_TIMEOUT_MS) : undefined
        const response = await fetch(`${baseUrl}/functions/v1/${functionName}${path}`, {
            method,
            headers,
            ...(controller ? { signal: controller.signal } : {}),
            ...(method === 'GET' ? {} : {
                body: isFormData ? options!.body as FormData : JSON.stringify(options?.body ?? {}),
            }),
        }).finally(() => { if (fetchTimeout) clearTimeout(fetchTimeout) })

        const responseBody = await response.json().catch(() => null)

        if (!response.ok) {
            console.error(`[API ERROR] ${functionName}`, { trace_id, status: response.status })
            const normalized = normalizeErrorEnvelope(responseBody, trace_id, response.status)

            return {
                data: null,
                error: {
                    message: normalized.message,
                    code: normalized.code,
                    trace_id: normalized.trace_id,
                    request_id: normalized.request_id,
                    status: normalized.status,
                },
            }
        }

        return { data: responseBody as T, error: null }
    } catch {
        console.error(`[API FATAL] ${functionName}`, { trace_id })

        return {
            data: null,
            error: {
                message: 'Network or protocol error.',
                trace_id,
            },
        }
    }
}

export async function invokePublicFunction<T>(
    functionName: string,
    options?: InvokeOptions
): Promise<ApiResponse<T>> {
    const trace_id = crypto.randomUUID()
    const method = options?.method ?? 'POST'
    const path = options?.path ? (options.path.startsWith('/') ? options.path : `/${options.path}`) : ''

    try {
        const baseUrl = import.meta.env.DEV ? '' : config.supabaseUrl
        const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData
        const headers: Record<string, string> = {
            apikey: config.supabaseAnonKey,
            'X-Trace-Id': trace_id,
            'X-Request-Id': trace_id,
        }
        if (!isFormData) {
            headers['Content-Type'] = 'application/json'
        }
        const controller = isFormData ? undefined : new AbortController()
        const fetchTimeout = controller ? setTimeout(() => controller.abort(), options?.timeoutMs ?? FETCH_TIMEOUT_MS) : undefined
        const response = await fetch(`${baseUrl}/functions/v1/${functionName}${path}`, {
            method,
            headers,
            ...(controller ? { signal: controller.signal } : {}),
            ...(method === 'GET' ? {} : {
                body: isFormData ? options!.body as FormData : JSON.stringify(options?.body ?? {}),
            }),
        }).finally(() => { if (fetchTimeout) clearTimeout(fetchTimeout) })

        const responseBody = await response.json().catch(() => null)

        if (!response.ok) {
            console.error(`[API ERROR] ${functionName}`, { trace_id, status: response.status })
            const normalized = normalizeErrorEnvelope(responseBody, trace_id, response.status)
            return {
                data: null,
                error: {
                    message: normalized.message,
                    code: normalized.code,
                    trace_id: normalized.trace_id,
                    request_id: normalized.request_id,
                    status: normalized.status,
                },
            }
        }

        return { data: responseBody as T, error: null }
    } catch {
        console.error(`[API FATAL] ${functionName}`, { trace_id })
        return {
            data: null,
            error: {
                message: 'Network or protocol error.',
                trace_id,
            },
        }
    }
}
